import { camelCase, findLast, upperFirst } from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  StyledH2,
  StyledText,
} from '../../../pages/UploadDocuments/UploadDocuments.styles';
import {
  DOCUMENTS_SIDES,
  TENANT_DOCS_DESCRIPTIONS,
  USERS_TYPES,
} from '../../../shared/constants';
import useGuestDocuments from '../../../shared/hooks/useBookingGuestDocuments';
import { IUserDocument } from '../../../shared/ts/document';
import { IGuest } from '../../../shared/ts/user';
import DocumentField from '../../DocumentField';
import { StyledH3 } from '../AddATenant/AddATenant.styles';
import { filterRequiredFieldForDocumentTenant } from './utils/requiredFieldForDocumentTenant';

const UploadTenantsDocuments = ({
  index,
  user_id,
  guest_id,
  occupation,
  guest,
  handleMissingTenantDocument,
}: {
  index: number;
  user_id: number;
  guest_id: string;
  occupation: string;
  bookingId?: string;
  guest?: IGuest;
  handleMissingTenantDocument: (
    index: number,
    missingFields: {
      label: string;
      fields: { label: string; sideLabel?: string }[];
    },
  ) => void;
}): JSX.Element => {
  const { t } = useTranslation(['common', 'documentsflow', 'form']);
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  const guestOccupation = camelCase(occupation ?? guest?.occupation);

  const isLoadingUserDocuments = useGuestDocuments(guest_id, bookingId!);
  const guestDocuments = guest?.documents;

  const idDocument = findLast(guestDocuments, {
    type: 'identity_document',
  }) as IUserDocument;

  const taxNumberDocument = findLast(guestDocuments, {
    type: 'tax_number',
  }) as IUserDocument;

  const residencyPermit = findLast(guestDocuments, {
    type: 'other',
    description: TENANT_DOCS_DESCRIPTIONS.RESIDENCE_PERMIT,
  }) as IUserDocument;

  const thirdLastPaycheck = guestDocuments
    ? findLast(guestDocuments, {
        type: 'proof_of_income',
        description: TENANT_DOCS_DESCRIPTIONS.THIRD_LAST_PAYCHECK,
      })
    : undefined;
  const secondLastPaycheck = guestDocuments
    ? findLast(guestDocuments, {
        type: 'proof_of_income',
        description: TENANT_DOCS_DESCRIPTIONS.SECOND_LAST_PAYCHECK,
      })
    : undefined;
  const lastPaycheck = guestDocuments
    ? findLast(guestDocuments, {
        type: 'proof_of_income',
        description: TENANT_DOCS_DESCRIPTIONS.LAST_PAYCHECK,
      })
    : undefined;
  const workContract = guestDocuments
    ? findLast(guestDocuments, {
        type: 'proof_of_income',
        description: TENANT_DOCS_DESCRIPTIONS.WORK_CONTRACT,
      })
    : undefined;
  const secondLastIncomeTaxReturn = guestDocuments
    ? findLast(guestDocuments, {
        type: 'proof_of_income',
        description: TENANT_DOCS_DESCRIPTIONS.SECOND_LAST_INCOME_TAX_RETURN,
      })
    : undefined;
  const lastIncomeTaxReturn = guestDocuments
    ? findLast(guestDocuments, {
        type: 'proof_of_income',
        description: TENANT_DOCS_DESCRIPTIONS.LAST_INCOME_TAX_RETURN,
      })
    : undefined;
  const bankAccountBalance = guestDocuments
    ? findLast(guestDocuments, {
        type: 'proof_of_income',
        description: TENANT_DOCS_DESCRIPTIONS.LATEST_ACCOUNT_STATEMENT,
      })
    : undefined;
  const penultimateBankAccountBalance = guestDocuments
    ? findLast(guestDocuments, {
        type: 'proof_of_income',
        description: TENANT_DOCS_DESCRIPTIONS.PENULTIMATE_ACCOUNT_STATEMENT,
      })
    : undefined;
  const prepenultimatebankAccountBalance = guestDocuments
    ? findLast(guestDocuments, {
        type: 'proof_of_income',
        description: TENANT_DOCS_DESCRIPTIONS.PREPENULTIMATE_ACCOUNT_STATEMENT,
      })
    : undefined;
  const lastPension = guestDocuments
    ? findLast(guestDocuments, {
        type: 'proof_of_income',
        description: TENANT_DOCS_DESCRIPTIONS.LAST_PENSION_SLIPS,
      })
    : undefined;
  const secondLastPension = guestDocuments
    ? findLast(guestDocuments, {
        type: 'proof_of_income',
        description: TENANT_DOCS_DESCRIPTIONS.SECOND_LAST_PENSION_SLIPS,
      })
    : undefined;
  const universityCertification = guestDocuments
    ? findLast(guestDocuments, {
        type: 'other',
        description: TENANT_DOCS_DESCRIPTIONS.UNIVERSITY_CERTIFICATION,
      })
    : undefined;

  useEffect(() => {
    const missingValue = filterRequiredFieldForDocumentTenant({
      index,
      occupation,
      guestDocuments,
    });
    handleMissingTenantDocument(index, missingValue ?? []);
  }, [occupation, guestDocuments]);

  return (
    <>
      {!isLoadingUserDocuments && occupation && (
        <>
          <StyledH2>
            {upperFirst(t('documentsflow:uploadYourDocuments'))}
          </StyledH2>
          <StyledText black>
            {upperFirst(t('documentsflow:documentsMaxSize'))}
          </StyledText>{' '}
          <br />
          <StyledH3>
            {upperFirst(
              occupation !== USERS_TYPES.FOREIGN_STUDENT
                ? t('idCardOrPassport')
                : t('passport'),
            )}
          </StyledH3>{' '}
          <DocumentField
            title={upperFirst(t('front'))}
            type="identity_document"
            documentUploaded={idDocument}
            documentSide={DOCUMENTS_SIDES.FRONT}
            ownerId={user_id}
            booking_id={bookingId!}
          />
          <DocumentField
            title={upperFirst(t('rear'))}
            type="identity_document"
            documentUploaded={idDocument}
            documentSide={DOCUMENTS_SIDES.REAR}
            ownerId={user_id}
            booking_id={bookingId!}
          />{' '}
          <>
            <br />
            <StyledH3>{upperFirst(t('fiscalCode'))}</StyledH3>
            <DocumentField
              title={upperFirst(t('front'))}
              type="tax_number"
              documentUploaded={taxNumberDocument}
              ownerId={user_id}
              booking_id={bookingId!}
            />{' '}
          </>
          {occupation === USERS_TYPES.FOREIGN_STUDENT && (
            <>
              <br />
              <StyledH3>{upperFirst(t('form:residencePermit'))}</StyledH3>{' '}
              <DocumentField
                title={upperFirst(t('front'))}
                type="other"
                documentUploaded={residencyPermit}
                documentSide={DOCUMENTS_SIDES.FRONT}
                description={TENANT_DOCS_DESCRIPTIONS.RESIDENCE_PERMIT}
                ownerId={user_id}
                booking_id={bookingId!}
              />
              <DocumentField
                title={upperFirst(t('rear'))}
                type="other"
                documentUploaded={residencyPermit}
                documentSide={DOCUMENTS_SIDES.REAR}
                description={TENANT_DOCS_DESCRIPTIONS.RESIDENCE_PERMIT}
                ownerId={user_id}
                booking_id={bookingId!}
              />{' '}
            </>
          )}{' '}
          {(occupation === USERS_TYPES.FOREIGN_STUDENT ||
            occupation === USERS_TYPES.ITALIAN_STUDENT) && (
            <>
              <br />
              <StyledH3>
                {upperFirst(t('form:universityCertification'))}
              </StyledH3>{' '}
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:universityCertification'))}
                type="other"
                documentUploaded={universityCertification}
                ownerId={user_id}
                description={TENANT_DOCS_DESCRIPTIONS.UNIVERSITY_CERTIFICATION}
                booking_id={bookingId!}
              />
              <br />
            </>
          )}{' '}
          {guestOccupation === USERS_TYPES.EMPLOYEE && (
            <>
              <br />
              <StyledH3>{upperFirst(t('form:workContract'))}</StyledH3>
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:workContract'))}
                type="proof_of_income"
                documentUploaded={workContract}
                ownerId={user_id}
                description={TENANT_DOCS_DESCRIPTIONS.WORK_CONTRACT}
                booking_id={bookingId!}
              />
              <br />
            </>
          )}{' '}
          {guestOccupation === USERS_TYPES.SELF_EMPLOYED && (
            <>
              <br />
              <StyledH3>{upperFirst(t('form:last2taxReturn'))}</StyledH3>
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:lastIncomeTaxReturn'))}
                type="proof_of_income"
                documentUploaded={lastIncomeTaxReturn}
                ownerId={user_id}
                description={TENANT_DOCS_DESCRIPTIONS.LAST_INCOME_TAX_RETURN}
                booking_id={bookingId!}
              />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:secondLastIncomeTaxReturn'))}
                type="proof_of_income"
                documentUploaded={secondLastIncomeTaxReturn}
                ownerId={user_id}
                description={
                  TENANT_DOCS_DESCRIPTIONS.SECOND_LAST_INCOME_TAX_RETURN
                }
                booking_id={bookingId!}
              />
              <br />
            </>
          )}{' '}
          {guestOccupation === USERS_TYPES.RETIRED && (
            <>
              <br />
              <StyledH3>{upperFirst(t('form:last2PensionSlips'))}</StyledH3>
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:lastPensionSlip'))}
                type="proof_of_income"
                documentUploaded={lastPension}
                ownerId={user_id}
                description={TENANT_DOCS_DESCRIPTIONS.LAST_PENSION_SLIPS}
                booking_id={bookingId!}
              />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:penultimatePensionSlip'))}
                type="proof_of_income"
                documentUploaded={secondLastPension}
                ownerId={user_id}
                description={TENANT_DOCS_DESCRIPTIONS.SECOND_LAST_PENSION_SLIPS}
                booking_id={bookingId!}
              />
              <br />
            </>
          )}{' '}
          {guestOccupation === USERS_TYPES.SELF_EMPLOYED && (
            <>
              <br />
              <StyledH3>{upperFirst(t('form:bankAccountBalance'))}</StyledH3>
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:latestAccountStatement'))}
                type="proof_of_income"
                documentUploaded={bankAccountBalance}
                ownerId={user_id}
                description={TENANT_DOCS_DESCRIPTIONS.LATEST_ACCOUNT_STATEMENT}
                booking_id={bookingId!}
              />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:penultimateAccountStatement'))}
                type="proof_of_income"
                documentUploaded={penultimateBankAccountBalance}
                ownerId={user_id}
                description={
                  TENANT_DOCS_DESCRIPTIONS.PENULTIMATE_ACCOUNT_STATEMENT
                }
                booking_id={bookingId!}
              />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:prepenultimateAccountStatement'))}
                type="proof_of_income"
                documentUploaded={prepenultimatebankAccountBalance}
                ownerId={user_id}
                description={
                  TENANT_DOCS_DESCRIPTIONS.PREPENULTIMATE_ACCOUNT_STATEMENT
                }
                booking_id={bookingId!}
              />
              <br />
            </>
          )}{' '}
          {guestOccupation === USERS_TYPES.EMPLOYEE && (
            <>
              <br />
              <StyledH3>{upperFirst(t('form:last3Paychecks'))}</StyledH3>
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:lastPaycheck'))}
                type="proof_of_income"
                documentUploaded={lastPaycheck}
                ownerId={user_id}
                description={TENANT_DOCS_DESCRIPTIONS.LAST_PAYCHECK}
                booking_id={bookingId!}
              />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:secondLastPaycheck'))}
                type="proof_of_income"
                documentUploaded={secondLastPaycheck}
                ownerId={user_id}
                description={TENANT_DOCS_DESCRIPTIONS.SECOND_LAST_PAYCHECK}
                booking_id={bookingId!}
              />
              <DocumentField
                documentSide={DOCUMENTS_SIDES.FRONT}
                title={upperFirst(t('form:thirdLastPaycheck'))}
                type="proof_of_income"
                documentUploaded={thirdLastPaycheck}
                ownerId={user_id}
                description={TENANT_DOCS_DESCRIPTIONS.THIRD_LAST_PAYCHECK}
                booking_id={bookingId!}
              />
              <br />
            </>
          )}{' '}
        </>
      )}{' '}
    </>
  );
};

export default UploadTenantsDocuments;
